import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { UserCog, Plus, Loader, AlertCircle, Check, Search, UserCheck } from 'lucide-react';

const GITHUB_ASSETS_API_BASE = 'https://api.github.com/repos/TokenRevolution/dios10_assets/contents';
const GITHUB_ASSETS_RAW_BASE = 'https://raw.githubusercontent.com/TokenRevolution/dios10_assets/main';

const COACH_RARITIES = [
  { value: 'plus', label: 'Plus' },
  { value: 'rare', label: 'Rare' },
  { value: 'epic', label: 'Epic' },
  { value: 'unique', label: 'Unique' }
];

const cardRowKey = (templateId, cardId) => `${templateId}:${cardId}`;

const RARITY_BADGE_CLASS = {
  plus: 'bg-blue-900/60 text-blue-200 border-blue-700',
  rare: 'bg-purple-900/60 text-purple-200 border-purple-700',
  epic: 'bg-amber-900/60 text-amber-200 border-amber-700',
  unique: 'bg-rose-900/60 text-rose-200 border-rose-700'
};

const normalizeCustomGithubImagePath = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('https://github.com/')) {
    return trimmed.replace('https://github.com/', 'https://raw.githubusercontent.com/').replace('/blob/', '/');
  }

  if (trimmed.startsWith('/custom/')) {
    return `${GITHUB_ASSETS_RAW_BASE}${trimmed}`;
  }

  if (trimmed.startsWith('custom/')) {
    return `${GITHUB_ASSETS_RAW_BASE}/${trimmed}`;
  }

  if (trimmed.startsWith('/main/custom/')) {
    return `${GITHUB_ASSETS_RAW_BASE}${trimmed.replace('/main', '')}`;
  }

  if (trimmed.startsWith('main/custom/')) {
    return `${GITHUB_ASSETS_RAW_BASE}/${trimmed.replace(/^main\//, '')}`;
  }

  return trimmed;
};

const AdminCoachCards = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [customName, setCustomName] = useState('');
  const [customImagePath, setCustomImagePath] = useState('');
  const [rarity, setRarity] = useState('plus');
  const [quantity, setQuantity] = useState('1');
  const [githubImages, setGithubImages] = useState([]);
  const [githubImagesLoading, setGithubImagesLoading] = useState(false);
  const [githubImagesError, setGithubImagesError] = useState('');
  const [selectedGithubPath, setSelectedGithubPath] = useState('');

  const [assigningCardId, setAssigningCardId] = useState(null);
  const [usernameSearch, setUsernameSearch] = useState({});
  const [userResults, setUserResults] = useState({});
  const [userSearchLoading, setUserSearchLoading] = useState({});
  const [selectedUser, setSelectedUser] = useState({});

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const loadCoachCards = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getCoachCards();
      if (response?.success) {
        const onlyLegend = (response.templates || []).filter(
          (t) => String(t.cardKind || 'legend').toLowerCase() !== 'divinity'
            && !String(t.customImagePath || '').toLowerCase().includes('/custom/divinity/')
        );
        setTemplates(onlyLegend);
      }
    } catch (err) {
      showMessage('error', err?.response?.data?.error || err.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoachCards();
  }, []);

  useEffect(() => {
    const loadGithubImages = async () => {
      try {
        setGithubImagesLoading(true);
        setGithubImagesError('');
        const response = await fetch(`${GITHUB_ASSETS_API_BASE}/custom/legend`);
        if (!response.ok) {
          throw new Error(`GitHub responded with ${response.status}`);
        }
        const items = await response.json();
        const imageExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];
        const images = (Array.isArray(items) ? items : [])
          .filter((item) => item?.type === 'file' && imageExtensions.some((ext) => String(item.name || '').toLowerCase().endsWith(ext)))
          .filter((item) => !String(item.path || '').toLowerCase().includes('/divinity/'))
          .map((item) => ({
            name: item.name,
            path: item.path,
            rawUrl: item.download_url || `${GITHUB_ASSETS_RAW_BASE}/${item.path}`
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setGithubImages(images);
      } catch (error) {
        console.error('Error loading GitHub legend images:', error);
        setGithubImages([]);
        setGithubImagesError('Impossibile caricare le immagini da GitHub');
      } finally {
        setGithubImagesLoading(false);
      }
    };
    loadGithubImages();
  }, []);

  const handleGithubImageSelect = (githubPath) => {
    setSelectedGithubPath(githubPath);
    if (!githubPath) {
      setCustomImagePath('');
      return;
    }
    const selected = githubImages.find((img) => img.path === githubPath);
    setCustomImagePath(selected ? selected.rawUrl : normalizeCustomGithubImagePath(githubPath));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!customName.trim()) {
      showMessage('error', 'Inserisci il nome della card');
      return;
    }
    const qty = parseInt(quantity, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      showMessage('error', 'La quantità deve essere maggiore di 0');
      return;
    }

    setSaving(true);
    try {
      const response = await adminApi.createCoachCard({
        customName: customName.trim(),
        customImagePath: customImagePath.trim() || undefined,
        rarity,
        quantity: qty
      });
      if (response?.success) {
        showMessage('success', `${qty} card allenatore create con successo`);
        setCustomName('');
        setCustomImagePath('');
        setSelectedGithubPath('');
        setQuantity('1');
        await loadCoachCards();
      }
    } catch (err) {
      showMessage('error', err?.response?.data?.error || err.message || 'Creazione fallita');
    } finally {
      setSaving(false);
    }
  };

  const searchUsersForCard = async (rowKey) => {
    const query = (usernameSearch[rowKey] || '').trim();
    if (query.length < 2) {
      setUserResults((prev) => ({ ...prev, [rowKey]: [] }));
      return;
    }
    setUserSearchLoading((prev) => ({ ...prev, [rowKey]: true }));
    try {
      const response = await adminApi.getUsers({ search: query }, 10, 0);
      if (response?.success) {
        setUserResults((prev) => ({ ...prev, [rowKey]: response.users || [] }));
      }
    } catch (err) {
      showMessage('error', err?.response?.data?.error || err.message || 'Ricerca utenti fallita');
    } finally {
      setUserSearchLoading((prev) => ({ ...prev, [rowKey]: false }));
    }
  };

  const handleAssign = async (templateId, cardId, rarityName) => {
    const rowKey = cardRowKey(templateId, cardId);
    const user = selectedUser[rowKey];
    if (!user?.id) {
      showMessage('error', 'Seleziona un utente dalla ricerca');
      return;
    }

    setAssigningCardId(rowKey);
    try {
      const response = await adminApi.assignCoachCardOwner(
        cardId,
        user.id,
        templateId,
        rarityName || undefined
      );
      if (response?.success) {
        showMessage('success', `Card assegnata a ${response.ownerUsername}`);
        setUsernameSearch((prev) => ({ ...prev, [rowKey]: '' }));
        setUserResults((prev) => ({ ...prev, [rowKey]: [] }));
        setSelectedUser((prev) => ({ ...prev, [rowKey]: null }));
        await loadCoachCards();
      }
    } catch (err) {
      showMessage('error', err?.response?.data?.error || err.message || 'Assegnazione fallita');
    } finally {
      setAssigningCardId(null);
    }
  };

  const imagePreviewUrl = normalizeCustomGithubImagePath(customImagePath);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <UserCog className="w-8 h-8 mr-3 text-indigo-400" />
          Card Allenatore
        </h1>
      </div>

      <p className="text-gray-400 text-sm mb-6 max-w-3xl">
        Crea card Legend personalizzate (<strong className="text-gray-200">Plus</strong>, <strong className="text-gray-200">Rare</strong>, <strong className="text-gray-200">Epic</strong> o <strong className="text-gray-200">Unique</strong>).
        Dopo la creazione, assegna ogni copia a un utente: la card apparirà in <strong className="text-gray-200">My Cards</strong> del profilo.
        Ogni copia è identificata da <strong className="text-gray-200">template + id</strong> (lo stesso numero id può esistere in tabelle Plus/Rare/Epic/Unique diverse).
      </p>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg border ${
          message.type === 'success'
            ? 'bg-green-900/50 border-green-700 text-green-200'
            : 'bg-red-900/50 border-red-700 text-red-200'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? <Check className="w-5 h-5 mr-2" /> : <AlertCircle className="w-5 h-5 mr-2" />}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-8">
        <h2 className="text-xl font-semibold mb-6 flex items-center">
          <Plus className="w-5 h-5 mr-2 text-indigo-400" />
          Crea card allenatore
        </h2>

        <form onSubmit={handleCreate} className="space-y-5 max-w-xl">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Tipo card</label>
            <select
              value={rarity}
              onChange={(e) => setRarity(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white"
              required
            >
              {COACH_RARITIES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Plus, Rare, Epic o Unique — come le altre card Legend custom della piattaforma.
            </p>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Nome</label>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white"
              placeholder="es. Mister X"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Immagine</label>
            <select
              value={selectedGithubPath}
              onChange={(e) => handleGithubImageSelect(e.target.value)}
              className="w-full px-4 py-2 mb-2 rounded-lg bg-gray-900 border border-gray-600 text-white disabled:opacity-60"
              disabled={githubImagesLoading}
            >
              <option value="">
                {githubImagesLoading ? 'Caricamento immagini...' : 'Seleziona da GitHub (custom/legend)...'}
              </option>
              {githubImages.map((img) => (
                <option key={img.path} value={img.path}>{img.name}</option>
              ))}
            </select>
            <input
              type="text"
              value={customImagePath}
              onChange={(e) => setCustomImagePath(e.target.value)}
              onBlur={() => setCustomImagePath((v) => normalizeCustomGithubImagePath(v))}
              className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white"
              placeholder="URL GitHub o path custom/legend/..."
            />
            {githubImagesError && (
              <p className="mt-1 text-xs text-red-300">{githubImagesError}</p>
            )}
            {imagePreviewUrl && (
              <div className="mt-3 flex items-start gap-4 rounded-lg border border-gray-700 bg-gray-900/50 p-3">
                <img
                  src={imagePreviewUrl}
                  alt="Anteprima"
                  className="h-24 w-24 rounded-lg object-cover border border-gray-700 bg-gray-800"
                />
                <p className="text-xs text-gray-400 break-all">{imagePreviewUrl}</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Quantità</label>
            <input
              type="number"
              min="1"
              max="50"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white"
              required
            />
            <p className="mt-1 text-xs text-gray-500">Ogni copia può essere assegnata a un utente diverso.</p>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg disabled:opacity-50"
          >
            {saving ? 'Creazione...' : 'Crea card'}
          </button>
        </form>
      </div>

      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-semibold mb-6">Card create</h2>

        {loading ? (
          <div className="text-center py-8">
            <Loader className="w-8 h-8 animate-spin mx-auto text-indigo-400" />
          </div>
        ) : templates.length === 0 ? (
          <p className="text-gray-400 text-sm">Nessuna card allenatore creata.</p>
        ) : (
          <div className="space-y-8">
            {templates.map((template) => (
              <div key={template.templateId} className="border border-gray-700 rounded-lg overflow-hidden">
                <div className="flex items-center gap-4 p-4 bg-gray-900/60 border-b border-gray-700">
                  {template.customImagePath && (
                    <img
                      src={normalizeCustomGithubImagePath(template.customImagePath)}
                      alt={template.customName}
                      className="h-16 w-16 rounded-lg object-cover border border-gray-600"
                    />
                  )}
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-white">{template.customName}</h3>
                      {template.rarityName && (
                        <span className={`text-xs px-2 py-0.5 rounded border uppercase font-medium ${
                          RARITY_BADGE_CLASS[template.rarityName] || 'bg-gray-700 text-gray-300 border-gray-600'
                        }`}>
                          {template.rarityDisplayName || template.rarityName}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">
                      {template.cards.length} copie · template #{template.templateId}
                    </p>
                  </div>
                </div>

                <div className="divide-y divide-gray-700">
                  {template.cards.map((card) => {
                    const rowKey = cardRowKey(template.templateId, card.id);
                    return (
                    <div key={rowKey} className="p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div>
                        <span className="text-sm text-gray-300">
                          Copia #{card.id} · {template.rarityDisplayName || template.rarityName}
                        </span>
                        {card.ownerId ? (
                          <p className="text-sm text-green-300 mt-1 flex items-center">
                            <UserCheck className="w-4 h-4 mr-1" />
                            Assegnata a <strong className="ml-1">{card.ownerUsername}</strong>
                          </p>
                        ) : (
                          <p className="text-sm text-amber-300 mt-1">Non assegnata</p>
                        )}
                      </div>

                      {!card.ownerId && (
                        <div className="flex flex-col sm:flex-row gap-2 sm:items-end min-w-0 lg:max-w-md">
                          <div className="flex-1 relative">
                            <label className="block text-xs text-gray-500 mb-1">Username utente</label>
                            <div className="relative">
                              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                              <input
                                type="text"
                                value={usernameSearch[rowKey] || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setUsernameSearch((prev) => ({ ...prev, [rowKey]: val }));
                                  setSelectedUser((prev) => ({ ...prev, [rowKey]: null }));
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    searchUsersForCard(rowKey);
                                  }
                                }}
                                className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white text-sm"
                                placeholder="Cerca username..."
                              />
                            </div>
                            {userSearchLoading[rowKey] && (
                              <p className="text-xs text-gray-500 mt-1">Ricerca...</p>
                            )}
                            {(userResults[rowKey] || []).length > 0 && !selectedUser[rowKey] && (
                              <ul className="absolute z-10 mt-1 w-full max-w-xs bg-gray-900 border border-gray-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                                {(userResults[rowKey] || []).map((u) => (
                                  <li key={u.id}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedUser((prev) => ({ ...prev, [rowKey]: u }));
                                        setUsernameSearch((prev) => ({ ...prev, [rowKey]: u.username }));
                                        setUserResults((prev) => ({ ...prev, [rowKey]: [] }));
                                      }}
                                      className="w-full text-left px-3 py-2 text-sm text-white hover:bg-gray-700"
                                    >
                                      {u.username}
                                      <span className="text-gray-500 ml-2 text-xs">{u.email}</span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {selectedUser[rowKey] && (
                              <p className="text-xs text-indigo-300 mt-1">
                                Selezionato: {selectedUser[rowKey].username}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => searchUsersForCard(rowKey)}
                            className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
                          >
                            Cerca
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAssign(
                              template.templateId,
                              card.id,
                              card.rarityName || template.rarityName
                            )}
                            disabled={assigningCardId === rowKey || !selectedUser[rowKey]}
                            className="px-4 py-2 text-sm bg-green-700 hover:bg-green-600 rounded-lg text-white font-medium disabled:opacity-50 whitespace-nowrap"
                          >
                            {assigningCardId === rowKey ? '...' : 'Conferma'}
                          </button>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCoachCards;
